import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Send, Users, Upload, Plus, Trash2, Search,
  Loader2, FileText, ListChecks, Eye, X, Check,
  User, Phone, MapPin, Instagram, Facebook, Twitter, Linkedin, Heart, Edit
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketHolderCRM() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState('profiles');

  // CRM state
  const [holders, setHolders] = useState([]);
  const [holdersTotal, setHoldersTotal] = useState(0);
  const [holderSearch, setHolderSearch] = useState('');
  const [selectedHolder, setSelectedHolder] = useState(null);
  const [holderStats, setHolderStats] = useState(null);
  const [editingHolder, setEditingHolder] = useState(false);
  const [holderForm, setHolderForm] = useState({});

  // Lists state
  const [lists, setLists] = useState([]);
  const [showListForm, setShowListForm] = useState(false);
  const [listForm, setListForm] = useState({ name: '', description: '' });
  const [selectedList, setSelectedList] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState({ name: '', phone: '' });

  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ name: '', subject: '', body_html: '', list_ids: [] });
  const [sending, setSending] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  useEffect(() => {
    if (!['admin', 'organization'].includes(user?.role)) { navigate('/'); return; }
    fetchHolders();
    fetchHolderStats();
    fetchLists();
    fetchCampaigns();
  }, []);

  // ===== CRM =====
  const fetchHolders = async (search = '') => {
    try {
      const res = await axios.get(`${API}/marketing/ticket-holders?limit=50&search=${encodeURIComponent(search)}`, { headers });
      setHolders(res.data.holders || []);
      setHoldersTotal(res.data.total || 0);
    } catch {}
  };

  const fetchHolderStats = async () => {
    try {
      const res = await axios.get(`${API}/marketing/ticket-holders/stats/overview`, { headers });
      setHolderStats(res.data);
    } catch {}
  };

  const openHolder = async (holderId) => {
    try {
      const res = await axios.get(`${API}/marketing/ticket-holders/${holderId}`, { headers });
      setSelectedHolder(res.data);
      setEditingHolder(false);
    } catch { toast.error('Failed to load profile'); }
  };

  const saveHolder = async () => {
    try {
      const res = await axios.put(`${API}/marketing/ticket-holders/${selectedHolder.holder_id}`, holderForm, { headers });
      setSelectedHolder(res.data);
      setEditingHolder(false);
      toast.success('Profile updated');
      fetchHolders(holderSearch);
    } catch { toast.error('Failed to update'); }
  };

  // ===== Lists =====
  const fetchLists = async () => {
    try {
      const res = await axios.get(`${API}/marketing/email-lists`, { headers });
      setLists(res.data.lists || []);
    } catch {}
  };

  const createList = async () => {
    if (!listForm.name) return;
    try {
      await axios.post(`${API}/marketing/email-lists`, listForm, { headers });
      toast.success('List created');
      setShowListForm(false);
      setListForm({ name: '', description: '' });
      fetchLists();
    } catch { toast.error('Failed to create list'); }
  };

  const deleteList = async (listId) => {
    try {
      await axios.delete(`${API}/marketing/email-lists/${listId}`, { headers });
      toast.success('List deleted');
      if (selectedList === listId) { setSelectedList(null); setSubscribers([]); }
      fetchLists();
    } catch { toast.error('Failed to delete'); }
  };

  const viewList = async (listId) => {
    setSelectedList(listId);
    try {
      const res = await axios.get(`${API}/marketing/email-lists/${listId}/subscribers?limit=200`, { headers });
      setSubscribers(res.data.subscribers || []);
    } catch { toast.error('Failed to load subscribers'); }
  };

  const uploadCSV = async (listId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/marketing/email-lists/${listId}/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Added ${res.data.added} contacts (${res.data.skipped} skipped)`);
      fetchLists();
      if (selectedList === listId) viewList(listId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  const updateSubscriber = async (subId) => {
    try {
      await axios.put(`${API}/marketing/subscribers/${subId}`, subForm, { headers });
      toast.success('Contact updated');
      setEditingSub(null);
      if (selectedList) viewList(selectedList);
    } catch { toast.error('Failed to update'); }
  };

  const deleteSubscriber = async (subId) => {
    try {
      await axios.delete(`${API}/marketing/subscribers/${subId}`, { headers });
      toast.success('Contact removed');
      if (selectedList) viewList(selectedList);
      fetchLists();
    } catch { toast.error('Failed to remove'); }
  };

  const createAutoList = async () => {
    try {
      const res = await axios.post(`${API}/marketing/email-lists/auto-all-holders`, {}, { headers });
      toast.success(`Created list with ${res.data.subscriber_count} ticket holders`);
      fetchLists();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ===== Campaigns =====
  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/marketing/campaigns`, { headers });
      setCampaigns(res.data.campaigns || []);
    } catch {}
  };

  const saveCampaign = async () => {
    if (!campaignForm.name || !campaignForm.subject) {
      toast.error('Name and subject required'); return;
    }
    try {
      if (editingCampaign) {
        await axios.put(`${API}/marketing/campaigns/${editingCampaign}`, campaignForm, { headers });
        toast.success('Campaign updated');
      } else {
        await axios.post(`${API}/marketing/campaigns`, campaignForm, { headers });
        toast.success('Campaign created');
      }
      setShowCampaignForm(false);
      setEditingCampaign(null);
      setCampaignForm({ name: '', subject: '', body_html: '', list_ids: [] });
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const sendCampaign = async (campaignId) => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/marketing/campaigns/${campaignId}/send`, {}, { headers });
      toast.success(`Sent to ${res.data.sent} subscribers`);
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const deleteCampaign = async (id) => {
    try {
      await axios.delete(`${API}/marketing/campaigns/${id}`, { headers });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleListInCampaign = (listId) => {
    setCampaignForm(prev => ({
      ...prev,
      list_ids: prev.list_ids.includes(listId) ? prev.list_ids.filter(id => id !== listId) : [...prev.list_ids, listId]
    }));
  };

  if (!['admin', 'organization'].includes(user?.role)) return null;

  return (
    <div className="min-h-screen bg-background pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 pt-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/events')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Ticket Holder CRM</h1>
            <p className="text-sm text-muted-foreground">Know your audience intimately — {holdersTotal} profiles</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="profiles" className="flex-1" data-testid="tab-profiles"><Heart className="h-4 w-4 mr-2" /> Profiles</TabsTrigger>
            <TabsTrigger value="lists" className="flex-1" data-testid="tab-lists"><ListChecks className="h-4 w-4 mr-2" /> Lists & Segments</TabsTrigger>
            <TabsTrigger value="blasts" className="flex-1" data-testid="tab-blasts"><Mail className="h-4 w-4 mr-2" /> Email Blasts</TabsTrigger>
          </TabsList>

          {/* ===== PROFILES TAB ===== */}
          <TabsContent value="profiles">
            {holderStats && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="crm-stats">
                <StatCard label="Total Holders" value={holderStats.total_holders} />
                <StatCard label="With Phone" value={holderStats.with_phone} />
                <StatCard label="With Address" value={holderStats.with_address} />
                <StatCard label="Social Linked" value={holderStats.with_social} />
                <StatCard label="Repeat Attendees" value={holderStats.repeat_attendees} />
                <StatCard label="Avg Profile %" value={`${holderStats.avg_profile_completeness}%`} />
              </div>
            )}

            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={holderSearch} onChange={e => setHolderSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') fetchHolders(holderSearch); }} placeholder="Search by name, email, or phone..." className="pl-10" data-testid="holder-search" />
              </div>
              <Button size="sm" onClick={() => fetchHolders(holderSearch)}><Search className="h-4 w-4" /></Button>
            </div>

            <div className="grid lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2 space-y-1.5 max-h-[65vh] overflow-y-auto" data-testid="holder-list">
                {holders.length === 0 ? (
                  <Card className="text-center py-8"><CardContent><Heart className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" /><p className="text-sm text-muted-foreground">No ticket holders found</p></CardContent></Card>
                ) : holders.map(h => (
                  <Card key={h.holder_id} className={`cursor-pointer transition-all ${selectedHolder?.holder_id === h.holder_id ? 'border-indigo-500 bg-indigo-500/5' : 'border-border hover:border-indigo-500/30'}`} onClick={() => openHolder(h.holder_id)} data-testid={`holder-${h.holder_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-indigo-400" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{h.name || 'Unnamed'}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{h.email}</p>
                        </div>
                        <div className="flex flex-col items-end gap-0.5">
                          <Badge variant="outline" className="text-[9px]">{h.ticket_count} tickets</Badge>
                          <div className="w-12 h-1 bg-muted/40 rounded-full overflow-hidden"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${h.profile_completeness}%` }} /></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="lg:col-span-3">
                {selectedHolder ? (
                  <Card className="border-border" data-testid="holder-detail">
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center"><User className="h-6 w-6 text-indigo-400" /></div>
                          <div>
                            <h3 className="font-bold text-lg">{selectedHolder.name || 'Unnamed'}</h3>
                            <p className="text-xs text-muted-foreground">{selectedHolder.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right"><p className="text-xs text-muted-foreground">Profile</p><p className="text-sm font-bold text-indigo-400">{selectedHolder.profile_completeness}%</p></div>
                          <Button size="sm" variant="outline" onClick={() => { setEditingHolder(!editingHolder); setHolderForm({ ...selectedHolder }); }} data-testid="edit-holder"><Edit className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>

                      {editingHolder ? (
                        <div className="space-y-3 border-t border-border pt-3">
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={holderForm.name || ''} onChange={e => setHolderForm(p => ({ ...p, name: e.target.value }))} className="h-8 text-sm" /></div>
                            <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={holderForm.phone || ''} onChange={e => setHolderForm(p => ({ ...p, phone: e.target.value }))} className="h-8 text-sm" /></div>
                            <div className="space-y-1"><Label className="text-xs">Address</Label><Input value={holderForm.address || ''} onChange={e => setHolderForm(p => ({ ...p, address: e.target.value }))} className="h-8 text-sm" /></div>
                            <div className="space-y-1"><Label className="text-xs">City</Label><Input value={holderForm.city || ''} onChange={e => setHolderForm(p => ({ ...p, city: e.target.value }))} className="h-8 text-sm" /></div>
                            <div className="space-y-1"><Label className="text-xs">State</Label><Input value={holderForm.state || ''} onChange={e => setHolderForm(p => ({ ...p, state: e.target.value }))} className="h-8 text-sm" /></div>
                            <div className="space-y-1"><Label className="text-xs">ZIP</Label><Input value={holderForm.zip || ''} onChange={e => setHolderForm(p => ({ ...p, zip: e.target.value }))} className="h-8 text-sm" /></div>
                            <div className="space-y-1"><Label className="text-xs">Schooling</Label><Input value={holderForm.schooling || ''} onChange={e => setHolderForm(p => ({ ...p, schooling: e.target.value }))} className="h-8 text-sm" placeholder="e.g. CU Boulder" /></div>
                            <div className="space-y-1"><Label className="text-xs">Music Interests (comma-separated)</Label><Input value={(holderForm.music_interests || []).join(', ')} onChange={e => setHolderForm(p => ({ ...p, music_interests: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} className="h-8 text-sm" placeholder="Hip-hop, Jazz, R&B" /></div>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div className="flex items-center gap-2"><Switch checked={holderForm.married === true} onCheckedChange={c => setHolderForm(p => ({ ...p, married: c }))} /><Label className="text-xs">Married</Label></div>
                            <div className="flex items-center gap-2"><Switch checked={holderForm.has_kids === true} onCheckedChange={c => setHolderForm(p => ({ ...p, has_kids: c }))} /><Label className="text-xs">Has Kids</Label></div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Social Media</Label>
                            <div className="grid sm:grid-cols-2 gap-2">
                              {['instagram', 'facebook', 'twitter', 'linkedin', 'tiktok'].map(platform => (
                                <div key={platform} className="flex items-center gap-2">
                                  <span className="text-xs capitalize w-16">{platform}</span>
                                  <Input value={(holderForm.social_media || {})[platform] || ''} onChange={e => setHolderForm(p => ({ ...p, social_media: { ...(p.social_media || {}), [platform]: e.target.value } }))} className="h-7 text-xs" placeholder="@handle or URL" />
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea value={holderForm.notes || ''} onChange={e => setHolderForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[60px] text-sm" /></div>
                          <div className="flex gap-2"><Button size="sm" onClick={saveHolder} data-testid="save-holder">Save Profile</Button><Button size="sm" variant="outline" onClick={() => setEditingHolder(false)}>Cancel</Button></div>
                        </div>
                      ) : (
                        <>
                          <div className="grid sm:grid-cols-2 gap-3 border-t border-border pt-3">
                            {selectedHolder.phone && <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-emerald-400" /><span>{selectedHolder.phone}</span></div>}
                            {selectedHolder.address && <div className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-amber-400" /><span>{selectedHolder.address}{selectedHolder.city ? `, ${selectedHolder.city}` : ''}{selectedHolder.state ? `, ${selectedHolder.state}` : ''} {selectedHolder.zip}</span></div>}
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="text-center p-2 bg-indigo-500/5 rounded-lg border border-indigo-500/20"><p className="text-lg font-bold">{selectedHolder.ticket_count}</p><p className="text-[10px] text-muted-foreground">Tickets</p></div>
                            <div className="text-center p-2 bg-emerald-500/5 rounded-lg border border-emerald-500/20"><p className="text-lg font-bold text-emerald-400">${selectedHolder.total_spend}</p><p className="text-[10px] text-muted-foreground">Total Spend</p></div>
                            <div className="text-center p-2 bg-amber-500/5 rounded-lg border border-amber-500/20"><p className="text-lg font-bold">{(selectedHolder.events_attended || []).length}</p><p className="text-[10px] text-muted-foreground">Events</p></div>
                          </div>
                          {(selectedHolder.events_attended || []).length > 0 && (
                            <div><h4 className="text-xs font-semibold mb-2">Events Attended</h4><div className="space-y-1">{selectedHolder.events_attended.map((ev, i) => (<div key={i} className="flex items-center gap-2 text-xs p-2 bg-muted/20 rounded"><span className="flex-1">{ev.name}</span><span className="text-muted-foreground">{ev.date}</span></div>))}</div></div>
                          )}
                          {((selectedHolder.music_interests || []).length > 0 || selectedHolder.married !== null || selectedHolder.schooling) && (
                            <div><h4 className="text-xs font-semibold mb-2">Interests & Lifestyle</h4><div className="flex flex-wrap gap-1">{(selectedHolder.music_interests || []).map((m, i) => (<Badge key={i} variant="outline" className="text-[10px]">{m}</Badge>))}{selectedHolder.married === true && <Badge className="bg-pink-500/20 text-pink-400 text-[10px]">Married</Badge>}{selectedHolder.has_kids === true && <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">Has Kids</Badge>}{selectedHolder.schooling && <Badge variant="outline" className="text-[10px]">{selectedHolder.schooling}</Badge>}</div></div>
                          )}
                          {Object.values(selectedHolder.social_media || {}).some(v => v) && (
                            <div><h4 className="text-xs font-semibold mb-2">Social Media</h4><div className="flex flex-wrap gap-2">{selectedHolder.social_media?.instagram && <SocialLink icon={Instagram} label="Instagram" value={selectedHolder.social_media.instagram} />}{selectedHolder.social_media?.facebook && <SocialLink icon={Facebook} label="Facebook" value={selectedHolder.social_media.facebook} />}{selectedHolder.social_media?.twitter && <SocialLink icon={Twitter} label="Twitter" value={selectedHolder.social_media.twitter} />}{selectedHolder.social_media?.linkedin && <SocialLink icon={Linkedin} label="LinkedIn" value={selectedHolder.social_media.linkedin} />}</div></div>
                          )}
                          {selectedHolder.notes && <div><h4 className="text-xs font-semibold mb-1">Notes</h4><p className="text-xs text-muted-foreground whitespace-pre-wrap">{selectedHolder.notes}</p></div>}
                        </>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-border text-center py-12"><CardContent><Heart className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" /><p className="text-sm text-muted-foreground">Select a ticket holder to view their profile</p><p className="text-xs text-muted-foreground mt-1">Know your audience intimately</p></CardContent></Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ===== LISTS TAB ===== */}
          <TabsContent value="lists">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Email Lists & Segments</h2>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={createAutoList} data-testid="auto-list-btn"><Users className="h-4 w-4 mr-1" /> Import All Holders</Button>
                <Button size="sm" onClick={() => setShowListForm(true)} data-testid="new-list-btn"><Plus className="h-4 w-4 mr-1" /> New List</Button>
              </div>
            </div>
            {showListForm && (
              <Card className="mb-4 border-indigo-500/20">
                <CardContent className="p-4 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">List Name</Label><Input value={listForm.name} onChange={e => setListForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. VIP Attendees" data-testid="list-name" /></div>
                    <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={listForm.description} onChange={e => setListForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional" /></div>
                  </div>
                  <div className="flex gap-2"><Button size="sm" onClick={createList} data-testid="save-list">Create List</Button><Button size="sm" variant="outline" onClick={() => setShowListForm(false)}>Cancel</Button></div>
                </CardContent>
              </Card>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2" data-testid="email-lists">
                {lists.length === 0 ? (
                  <Card className="text-center py-8"><CardContent><ListChecks className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" /><p className="text-sm text-muted-foreground">No lists yet</p></CardContent></Card>
                ) : lists.map(l => (
                  <Card key={l.list_id} className={`transition-colors ${selectedList === l.list_id ? 'border-indigo-500' : 'border-border hover:border-indigo-500/30'}`}>
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-2 cursor-pointer" onClick={() => viewList(l.list_id)}>
                        <div className="flex-1 min-w-0"><p className="font-medium text-sm">{l.name}</p><p className="text-[10px] text-muted-foreground">{l.description || l.type}</p></div>
                        <Badge variant="outline" className="text-xs">{l.subscriber_count}</Badge>
                      </div>
                      <div className="flex items-center gap-2 pt-1 border-t border-border/50">
                        <Button size="sm" variant="outline" className="text-xs h-7 flex-1" onClick={() => { const input = document.createElement('input'); input.type='file'; input.accept='.csv'; input.onchange=(ev) => { if(ev.target.files[0]) uploadCSV(l.list_id, ev.target.files[0]); }; input.click(); }} data-testid={`upload-${l.list_id}`}><Upload className="h-3 w-3 mr-1" /> Upload CSV</Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => deleteList(l.list_id)} data-testid={`delete-list-${l.list_id}`}><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card className="border-border">
                <CardContent className="p-4">
                  {selectedList ? (
                    <>
                      <h3 className="text-sm font-semibold mb-3">Subscribers ({subscribers.length})</h3>
                      <div className="space-y-1.5 max-h-[50vh] overflow-y-auto" data-testid="subscriber-list">
                        {subscribers.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-4 text-center">No subscribers. Upload a CSV or add manually.</p>
                        ) : subscribers.map(s => (
                          <div key={s.subscriber_id} className="p-2.5 rounded border border-border text-xs" data-testid={`sub-${s.subscriber_id}`}>
                            {editingSub === s.subscriber_id ? (
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                  <Input value={subForm.name} onChange={e => setSubForm(p => ({ ...p, name: e.target.value }))} placeholder="Name" className="h-7 text-xs" data-testid="edit-sub-name" />
                                  <Input value={subForm.phone} onChange={e => setSubForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="h-7 text-xs" data-testid="edit-sub-phone" />
                                </div>
                                <div className="flex gap-1"><Button size="sm" className="h-6 text-[10px] px-2" onClick={() => updateSubscriber(s.subscriber_id)} data-testid="save-sub">Save</Button><Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setEditingSub(null)}>Cancel</Button></div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0"><p className="font-medium truncate">{s.name || 'Unnamed'}</p><p className="text-muted-foreground truncate">{s.email}</p>{s.phone && <p className="text-muted-foreground truncate">{s.phone}</p>}</div>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { setEditingSub(s.subscriber_id); setSubForm({ name: s.name || '', phone: s.phone || '' }); }} data-testid={`edit-sub-${s.subscriber_id}`}><Edit className="h-3 w-3" /></Button>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => deleteSubscriber(s.subscriber_id)} data-testid={`del-sub-${s.subscriber_id}`}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8"><Users className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" /><p className="text-sm text-muted-foreground">Select a list to view subscribers</p></div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== EMAIL BLASTS TAB ===== */}
          <TabsContent value="blasts">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Email Campaigns</h2>
              <Button size="sm" onClick={() => { setShowCampaignForm(true); setEditingCampaign(null); setCampaignForm({ name: '', subject: '', body_html: '', list_ids: [] }); }} data-testid="new-campaign-btn"><Plus className="h-4 w-4 mr-1" /> New Campaign</Button>
            </div>
            {showCampaignForm && (
              <Card className="mb-6 border-indigo-500/20">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between"><h3 className="font-semibold">{editingCampaign ? 'Edit Campaign' : 'New Email Campaign'}</h3><Button variant="ghost" size="icon" onClick={() => setShowCampaignForm(false)}><X className="h-4 w-4" /></Button></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Campaign Name</Label><Input value={campaignForm.name} onChange={e => setCampaignForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Early Bird Announcement" data-testid="campaign-name" /></div>
                    <div className="space-y-2"><Label>Email Subject</Label><Input value={campaignForm.subject} onChange={e => setCampaignForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. You're Invited!" data-testid="campaign-subject" /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email Body (HTML) — Use {"{{name}}"} for personalization</Label>
                    <Textarea value={campaignForm.body_html} onChange={e => setCampaignForm(p => ({ ...p, body_html: e.target.value }))} placeholder="<h2>Hey {{name}}!</h2><p>You're invited...</p>" className="min-h-[150px] font-mono text-sm" data-testid="campaign-body" />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Lists</Label>
                    {lists.length === 0 ? <p className="text-xs text-muted-foreground">No lists yet. Create one in the Lists tab.</p> : (
                      <div className="space-y-1.5">{lists.map(l => (
                        <label key={l.list_id} className="flex items-center gap-2 p-2 rounded border border-border cursor-pointer hover:bg-muted/30">
                          <input type="checkbox" checked={campaignForm.list_ids.includes(l.list_id)} onChange={() => toggleListInCampaign(l.list_id)} className="rounded" />
                          <span className="text-sm flex-1">{l.name}</span>
                          <Badge variant="outline" className="text-[10px]">{l.subscriber_count} subs</Badge>
                        </label>
                      ))}</div>
                    )}
                  </div>
                  <div className="flex gap-2"><Button onClick={saveCampaign} data-testid="save-campaign"><FileText className="h-4 w-4 mr-1" /> {editingCampaign ? 'Update' : 'Save Draft'}</Button><Button variant="outline" onClick={() => setShowCampaignForm(false)}>Cancel</Button></div>
                </CardContent>
              </Card>
            )}
            <div className="space-y-3" data-testid="campaign-list">
              {campaigns.length === 0 ? (
                <Card className="text-center py-10"><CardContent><Mail className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No campaigns yet</p></CardContent></Card>
              ) : campaigns.map(c => (
                <Card key={c.campaign_id} className="border-border hover:border-indigo-500/30 transition-colors">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.status === 'sent' ? 'bg-emerald-500/10' : c.status === 'sending' ? 'bg-amber-500/10' : 'bg-indigo-500/10'}`}>
                      {c.status === 'sent' ? <Check className="h-5 w-5 text-emerald-400" /> : c.status === 'sending' ? <Loader2 className="h-5 w-5 text-amber-400 animate-spin" /> : <Mail className="h-5 w-5 text-indigo-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{c.name}</p>
                      <p className="text-xs text-muted-foreground truncate">Subject: {c.subject}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant={c.status === 'sent' ? 'default' : 'outline'} className="text-[10px] capitalize">{c.status}</Badge>
                        {c.status === 'sent' && <span className="text-[10px] text-muted-foreground">Sent {c.stats?.sent || 0} / {c.stats?.total || 0}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {c.status === 'draft' && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingCampaign(c.campaign_id); setCampaignForm({ name: c.name, subject: c.subject, body_html: c.body_html, list_ids: c.list_ids || [] }); setShowCampaignForm(true); }}><Edit className="h-3.5 w-3.5" /></Button>
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={() => sendCampaign(c.campaign_id)} disabled={sending} data-testid={`send-${c.campaign_id}`}><Send className="h-3.5 w-3.5 mr-1" /> Send</Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteCampaign(c.campaign_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (<div className="p-3 rounded-lg border border-border bg-muted/20 text-center"><p className="text-lg font-bold">{value}</p><p className="text-[10px] text-muted-foreground">{label}</p></div>);
}

function SocialLink({ icon: Icon, label, value }) {
  const url = value.startsWith('http') ? value : `https://${label.toLowerCase()}.com/${value.replace('@', '')}`;
  return (<a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2 py-1 rounded border border-border text-xs hover:border-indigo-500/30 transition-colors"><Icon className="h-3.5 w-3.5" /><span className="truncate max-w-[100px]">{value}</span></a>);
}
